import React, { useState } from "react";
import styles from "./Newsletter.module.css";
import { Button } from "../Button/Button";
import { trackEvent } from "../../hooks/useGTM";

interface NewsletterProps {
  title: string;
  text: string;
  buttonText: string;
  iconUrl?: string;
}

const Newsletter: React.FC<NewsletterProps> = ({
  title,
  text,
  buttonText,
  iconUrl,
}) => {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Placeholder for future API implementation
      // TODO: Implement API call to collect email leads
      console.log("Email to subscribe:", email);

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Track newsletter subscription
      trackEvent("newsletter_subscribe", {
        location: "home_page",
        method: "footer_form",
        email_domain: email.split("@")[1],
      });

      // Success handling
      setSubscribed(true);
      setEmail("");

      // Reset the subscribed message after 5 seconds
      setTimeout(() => {
        setSubscribed(false);
      }, 5000);
    } catch (err) {
      setError("An error occurred while subscribing. Please try again.");
      console.error("Newsletter subscription error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={styles.newsletter}>
      <div className={styles.container}>
        <div className={styles.content}>
          {iconUrl && (
            <div className={styles.iconContainer}>
              <img
                src={iconUrl}
                alt="Newsletter icon"
                className={styles.icon}
              />
            </div>
          )}

          <div className={styles.textContent}>
            <h2 className={styles.title}>{title}</h2>
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: text }}
            />
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputContainer}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className={styles.input}
                aria-label="Your email address"
                disabled={loading}
              />
              <Button
                type="submit"
                className={styles.button}
                disabled={loading}
              >
                {loading ? "Subscribing..." : buttonText}
              </Button>
            </div>

            {error && <p className={styles.error}>{error}</p>}
            {subscribed && (
              <p className={styles.success}>Thank you for subscribing!</p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
